import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AppButton from "./AppButton.jsx";
import { Z_INDEX } from "./zIndex.js";
import {
  entityInfoBlockEditButtonClassName,
  entityMoreMenuItemClassName,
} from "../../design/tokens.js";

const MENU_WIDTH = 224;

export function useEntityCreateMenu() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: MENU_WIDTH });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const toggle = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const left = Math.max(8, Math.min(r.right - MENU_WIDTH, vw - MENU_WIDTH - 8));
    setPos({ top: r.bottom + 8, left, width: MENU_WIDTH });
    setOpen((v) => !v);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return { open, pos, btnRef, menuRef, toggle, close };
}

/** Erstellen-Dropdown — identisch auf Kunde- und Objekt-Detail. */
export default function EntityCreateMenu({
  items = [],
  onSelect,
  title = "Neu erstellen",
  onOpenChange = null,
}) {
  const { open, pos, btnRef, menuRef, toggle, close } = useEntityCreateMenu();

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const handleSelect = (target) => {
    onSelect?.(target);
    close();
  };

  if (!items.length) return null;

  return (
    <div className="relative shrink-0">
      <AppButton
        ref={btnRef}
        type="button"
        variant="primary"
        size="sm"
        className={entityInfoBlockEditButtonClassName}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        title={title}
      >
        Erstellen
      </AppButton>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label="Erstellen-Menü"
              style={{
                position: "fixed",
                top: pos.top,
                left: pos.left,
                width: pos.width,
                zIndex: Z_INDEX.selectPortal,
                pointerEvents: "auto",
              }}
              className="rounded-xl border border-default shadow-xl p-1 bg-surface-elevated"
            >
              {items.map((item) => (
                <AppButton
                  key={item.target}
                  type="button"
                  variant="ghost"
                  role="menuitem"
                  onClick={() => handleSelect(item.target)}
                  className={entityMoreMenuItemClassName}
                >
                  {item.label}
                </AppButton>
              ))}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
